import { ReactNode, useState } from 'react';
import { Ribbon, Text, ColorScheme, ColorButton, Box } from '@bitrise/bitkit';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';

type Notification = {
  id: string;
  name: string;
};

type NotificationsResponse = {
  notifications: Notification[];
};

const NotificationContainer = ({
  notificationId,
  isDismissible,
  children,
  ribbonColor,
}: {
  notificationId: string;
  isDismissible: boolean;
  children: ReactNode;
  ribbonColor: ColorScheme;
}) => {
  const [isClosed, setIsClosed] = useState(false);

  const { mutate: dismissBanner } = useMonolithApiMutation<unknown, unknown, { isDismissed: boolean }>(
    `/notifications/${notificationId}`,
    'PATCH',
  );

  if (isClosed) {
    return null;
  }

  const onClose = () => {
    setIsClosed(true);
    dismissBanner({ isDismissed: true });
  };

  return (
    <Ribbon data-testid="notification-ribbon" colorScheme={ribbonColor} onClose={isDismissible ? onClose : undefined}>
      {children}
    </Ribbon>
  );
};

export const KNOWN_BANNERS = {
  'platform-consumption-p75-banner': {
    isDismissible: true,
    ribbonColor: 'yellow' as const,
    content: (
      <Text>
        <Text size="4" as="span" fontWeight="bold">
          75% of infrastructure utilization limit reached.
        </Text>{' '}
        <Text size="4" as="span">
          Builds will not start upon reaching the limit.
        </Text>
      </Text>
    ),
  },
  'platform-consumption-p85-banner': {
    isDismissible: true,
    ribbonColor: 'yellow' as const,
    content: (
      <Text>
        <Text size="4" as="span" fontWeight="bold">
          85% of infrastructure utilization limit reached.
        </Text>{' '}
        <Text size="4" as="span">
          Builds will not start upon reaching the limit.
        </Text>
      </Text>
    ),
  },
  'platform-consumption-p100-banner': {
    isDismissible: false,
    ribbonColor: 'yellow' as const,
    content: (
      <Text>
        <Text size="4" as="span" fontWeight="bold">
          Infrastructure utilization limit reached.
        </Text>{' '}
        <Text size="4" as="span">
          Builds are suspended until the billing cycle resets.
        </Text>
      </Text>
    ),
  },
  'browserstack-partnership-offer-banner': {
    isDismissible: true,
    ribbonColor: 'purple' as const,
    content: (
      <Box display="flex" alignItems="center">
        <Text>
          <Text size="4" as="span" fontWeight="bold">
            Partner offer:
          </Text>{' '}
          <Text size="4" as="span">
            Unlock your free access to real device testing in BrowserStack App Automate
          </Text>
        </Text>
        <ColorButton
          as="a"
          href="https://www.browserstack.com/bitrise-partnership"
          colorScheme="purple"
          size="sm"
          marginLeft="16px"
        >
          Go to BrowserStack
        </ColorButton>
      </Box>
    ),
  },
};

const DynamicBanners = () => {
  const {
    data: { notifications = [] } = {},
    isLoading,
    isError,
  } = useMonolithApiQuery<NotificationsResponse, unknown>(['notifications'], '/notifications');

  if (isLoading || isError) {
    return null;
  }

  return Object.entries(KNOWN_BANNERS).map(([key, banner]) => {
    const notification = notifications.find(({ name }) => key === name);
    if (!notification) {
      return null;
    }

    return (
      <NotificationContainer
        key={notification.id}
        notificationId={notification.id}
        isDismissible={banner.isDismissible}
        ribbonColor={banner.ribbonColor}
      >
        {banner.content}
      </NotificationContainer>
    );
  });
};

export default DynamicBanners;
